import type { PropTypesTypes, DocumentedPropType, ReactDescObjT, PropTypesObj } from './types.js';

const addPropTypeDocumentationField = <T extends keyof ReactDescObjT>(fieldName: T) =>
  function addFieldToReactDesc(this: DocumentedPropType, value: ReactDescObjT[T]): DocumentedPropType {
    if (!this.reactDesc) {
      this.reactDesc = {};
    }
    let realValue = value;
    if (
      fieldName === 'global' ||
      fieldName === 'dataTestId' ||
      fieldName === 'hidden' ||
      fieldName === 'xstyled' ||
      fieldName === 'omitValidation'
    )
      realValue = true;

    this.reactDesc[fieldName] = realValue;
    return this;
  };

const documentedPropType: Omit<DocumentedPropType, 'type' | 'isRequired' | 'reactDesc'> = {
  defaultValue: addPropTypeDocumentationField('defaultValue'),
  description: addPropTypeDocumentationField('description'),
  deprecated: addPropTypeDocumentationField('deprecated'),
  format: addPropTypeDocumentationField('format'),
  signature: addPropTypeDocumentationField('signature'),
  global: addPropTypeDocumentationField('global'),
  omitValidation: addPropTypeDocumentationField('omitValidation'),
  hidden: addPropTypeDocumentationField('hidden'),
  dataTestId: addPropTypeDocumentationField('dataTestId'),
  xstyled: addPropTypeDocumentationField('xstyled'),
  isRequiredIf: addPropTypeDocumentationField('isRequiredIf'),
};

const createPropType = (type: PropTypesTypes) => {
  const propTypeObj = {
    type,
    ...documentedPropType,
  };
  Object.defineProperty(propTypeObj, 'isRequired', {
    get: function getRequired(this: DocumentedPropType) {
      if (!this.reactDesc) {
        this.reactDesc = {};
      }
      this.reactDesc.required = true;
      return this;
    },
    enumerable: true,
    configurable: true,
  });

  return propTypeObj;
};

const createPropTypeWithArgs = (type: PropTypesTypes) => (args: unknown) => {
  const propTypeObj = {
    args,
    type,
    ...documentedPropType,
  };
  Object.defineProperty(propTypeObj, 'isRequired', {
    get: function getRequired(this: DocumentedPropType) {
      if (!this.reactDesc) {
        this.reactDesc = {};
      }
      this.reactDesc.required = true;
      return this;
    },
    enumerable: true,
    configurable: true,
  });
  return propTypeObj;
};

const PropTypes = {
  custom: (callback: CallableFunction) => {
    const target = callback.bind(null) as CallableFunction & DocumentedPropType;
    target.type = 'func';
    Object.keys(documentedPropType).forEach((fieldName) => {
      const fieldNameCasted = fieldName as keyof DocumentedPropType;
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
      (target[fieldNameCasted] as unknown) = documentedPropType[fieldNameCasted] as unknown;
    });
    return target;
  },
} as unknown as PropTypesObj;

function definePropType(type: PropTypesTypes) {
  Object.defineProperty(PropTypes, type, {
    get: function getPropType() {
      return createPropType(type);
    },
    enumerable: true,
    configurable: true,
  });
}

function definePropTypeWithArgs(type: PropTypesTypes) {
  Object.defineProperty(PropTypes, type, {
    get: function getPropType() {
      return createPropTypeWithArgs(type);
    },
    enumerable: true,
    configurable: true,
  });
}

definePropType('any');
definePropType('array');
definePropType('bool');
definePropType('element');
definePropType('func');
definePropType('node');
definePropType('number');
definePropType('object');
definePropType('symbol');
definePropType('string');

definePropTypeWithArgs('arrayOf');
definePropTypeWithArgs('instanceOf');
definePropTypeWithArgs('objectOf');
definePropTypeWithArgs('oneOfType');
definePropTypeWithArgs('oneOf');
definePropTypeWithArgs('shape');
definePropTypeWithArgs('tuple');

export default PropTypes;
